/**
 * APX WorkCenter — FileDropZone
 * Drag-and-drop file transfer panel.
 * - Team Member tab: sends via internal Hub notification (unchanged)
 * - Client / External tab: sends via Email, Text (MMS), or Both using real delivery
 */

import { useState, useRef, useCallback, useEffect } from "react";
import { trpc } from "@/lib/trpc";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

// ─── Static non-bookkeeper recipients (always shown) ────────────────────────
// SOP: Bookkeepers are added dynamically from DB (team.listBookkeepers) inside the component
const STATIC_RECIPIENTS = [
  { label: "All Bkkprs", value: "role:bookkeeper", group: true },
  { label: "Jany Lopez — Bkkpr Mgr", value: "email:lopez.jany@gmail.com" },
  { label: "Gus — Controller", value: "email:gus@bbnfi.com" },
  { label: "Amy — Controller", value: "email:amybyfield@gmail.com" },
  { label: "Mr. B — Director", value: "email:byjosiah@gmail.com" },
  { label: "Jasmin — Tax Division", value: "email:jasminsilva91@gmail.com" },
];

function formatBytes(bytes: number) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1048576).toFixed(1)} MB`;
}

interface FileItem {
  file: File;
  id: string;
}

type DeliveryMethod = "email" | "text" | "both";

interface Props {
  defaultRecipient?: string;
  clientName?: string;
  compact?: boolean;
}

export default function FileDropZone({ defaultRecipient = "", clientName = "", compact = false }: Props) {
  const { user } = useAuth();
  // SOP: Live bookkeeper list from DB — auto-includes new bookkeepers added via Team & Access
  const { data: liveBookkeepers = [] } = trpc.team.listBookkeepers.useQuery(undefined, { refetchInterval: 60_000, staleTime: 30_000 });
  // Build full recipient list: static roles + live bookkeepers from DB
  const TEAM_RECIPIENTS = [
    ...STATIC_RECIPIENTS,
    ...liveBookkeepers
      .filter(bk => bk.role === "bookkeeper" || bk.role === "bookkeeper_supervisor")
      .map(bk => ({ label: `${bk.name} — Bkkpr`, value: `email:${bk.email}` })),
  ];
  const [dragging, setDragging] = useState(false);
  const [files, setFiles] = useState<FileItem[]>([]);
  const [selectedRecipients, setSelectedRecipients] = useState<Set<string>>(
    defaultRecipient ? new Set([defaultRecipient]) : new Set()
  );
  const [sendToClient, setSendToClient] = useState(false);

  // Client delivery fields
  const [deliveryMethod, setDeliveryMethod] = useState<DeliveryMethod>("both");
  const [clientEmail, setClientEmail] = useState("");
  const [clientPhone, setClientPhone] = useState("");
  const [clientDisplayName, setClientDisplayName] = useState(clientName || "");

  const [note, setNote] = useState("");
  const [uploading, setUploading] = useState(false);
  const [recipientOpen, setRecipientOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const [pasting, setPasting] = useState(false);

  const upload = trpc.files.upload.useMutation({
    onError: (err) => toast.error("Upload failed", { description: err.message }),
  });

  const sendFileToClient = trpc.communications.sendFileToClient.useMutation({
    onError: (err) => toast.error("Send failed", { description: err.message }),
  });

  // Tax document keyword detection
  const TAX_DOC_KEYWORDS = /\b(1040|1040a|1040ez|1040nr|1040x|w-2|w2|ssn|social.?security|tax.?return|schedule.?[abcde]|form.?4868|form.?8879|form.?8962|1099|k-1|k1)\b/i;

  const isTaxDocument = (fileName: string) => TAX_DOC_KEYWORDS.test(fileName);

  const addFiles = (incoming: File[]) => {
    const MAX_SIZE = 16 * 1024 * 1024;
    const valid = incoming.filter((f) => {
      if (f.size > MAX_SIZE) { toast.error(`${f.name} exceeds 16MB limit`); return false; }
      return true;
    });
    // Auto-switch to Text-only for tax documents
    if (sendToClient && valid.some((f) => isTaxDocument(f.name))) {
      setDeliveryMethod("text");
      toast.warning("Tax document detected — switched to Text (MMS) only", {
        description: "APX policy: tax documents with personal information must not be sent via email.",
        duration: 6000,
      });
    }
    setFiles((prev) => [
      ...prev,
      ...valid.map((f) => ({ file: f, id: `${f.name}-${Date.now()}-${Math.random()}` })),
    ]);
  };

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragging(false);
    addFiles(Array.from(e.dataTransfer.files));
  }, []);

  // ── Clipboard paste ────────────────────────────────────────────────────────
  useEffect(() => {
    const handlePaste = (e: ClipboardEvent) => {
      const items = e.clipboardData?.items;
      if (!items) return;
      const pastedFiles: File[] = [];
      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        if (item.kind === "file") {
          const file = item.getAsFile();
          if (file) pastedFiles.push(file);
        }
      }
      if (pastedFiles.length > 0) {
        e.preventDefault();
        setPasting(true);
        addFiles(pastedFiles);
        setTimeout(() => setPasting(false), 1200);
        toast.success(`${pastedFiles.length} file${pastedFiles.length > 1 ? "s" : ""} pasted from clipboard`);
      }
    };
    window.addEventListener("paste", handlePaste);
    return () => window.removeEventListener("paste", handlePaste);
  }, []);

  const toggleRecipient = (value: string) => {
    setSelectedRecipients((prev) => {
      const next = new Set(prev);
      if (next.has(value)) next.delete(value);
      else next.add(value);
      return next;
    });
  };

  const toggleAll = () => {
    if (selectedRecipients.size === TEAM_RECIPIENTS.length) {
      setSelectedRecipients(new Set());
    } else {
      setSelectedRecipients(new Set(TEAM_RECIPIENTS.map((r) => r.value)));
    }
  };

  // Validate client delivery readiness
  const clientCanSend = (() => {
    if (!sendToClient) return false;
    if (deliveryMethod === "email") return !!clientEmail.trim();
    if (deliveryMethod === "text") return !!clientPhone.trim();
    return !!clientEmail.trim() && !!clientPhone.trim();
  })();

  const effectiveRecipients = sendToClient
    ? (clientEmail ? [`email:${clientEmail.trim()}`] : [])
    : Array.from(selectedRecipients);

  const canSend = files.length > 0 && !uploading && (
    sendToClient ? clientCanSend : effectiveRecipients.length > 0
  );

  const handleSend = async () => {
    if (!canSend || !user) return;
    setUploading(true);

    try {
      if (sendToClient) {
        // ── Client delivery: Email / Text / Both ──────────────────────────
        for (const { file } of files) {
          const reader = new FileReader();
          const base64 = await new Promise<string>((resolve, reject) => {
            reader.onload = () => resolve((reader.result as string).split(",")[1]);
            reader.onerror = reject;
            reader.readAsDataURL(file);
          });
          const result = await sendFileToClient.mutateAsync({
            senderName: user.name,
            senderEmail: user.email ?? "",
            clientName: clientDisplayName || "Client",
            fileName: file.name,
            fileSize: file.size,
            mimeType: file.type || "application/octet-stream",
            fileDataBase64: base64,
            note: note || undefined,
            deliveryMethod,
            clientEmail: clientEmail.trim() || undefined,
            clientPhone: clientPhone.trim() || undefined,
          });
          const channels: string[] = [];
          if (result.results.email === "sent") channels.push("email");
          if (result.results.text === "sent") channels.push("text");
          const channelLabel = channels.length > 0 ? channels.join(" & ") : deliveryMethod;
          toast.success(`${file.name} sent via ${channelLabel}`, {
            description: clientDisplayName || clientEmail || clientPhone,
          });
        }
      } else {
        // ── Team member delivery (unchanged) ─────────────────────────────
        for (const { file } of files) {
          const reader = new FileReader();
          const base64 = await new Promise<string>((resolve, reject) => {
            reader.onload = () => resolve((reader.result as string).split(",")[1]);
            reader.onerror = reject;
            reader.readAsDataURL(file);
          });
          for (const recipientValue of effectiveRecipients) {
            const isRole = recipientValue.startsWith("role:");
            await upload.mutateAsync({
              senderName: user.name,
              senderRole: user.role,
              recipientEmail: isRole ? undefined : recipientValue.replace("email:", ""),
              recipientRole: isRole ? recipientValue.replace("role:", "") : undefined,
              fileName: file.name,
              fileSize: file.size,
              mimeType: file.type || "application/octet-stream",
              fileDataBase64: base64,
              clientName: clientName || undefined,
              note: note || undefined,
            });
          }
        }
        const recipientSummary = effectiveRecipients.length === 1
          ? (TEAM_RECIPIENTS.find((r) => r.value === effectiveRecipients[0])?.label ?? effectiveRecipients[0])
          : `${effectiveRecipients.length} recipients`;
        toast.success(`${files.length} file${files.length > 1 ? "s" : ""} sent`, {
          description: `Delivered to ${recipientSummary}`,
        });
      }
    } finally {
      setFiles([]);
      setNote("");
      if (!sendToClient) setSelectedRecipients(new Set());
      setUploading(false);
    }
  };

  const selectorLabel = selectedRecipients.size === 0
    ? "— Select recipients —"
    : selectedRecipients.size === 1
      ? (TEAM_RECIPIENTS.find((r) => r.value === Array.from(selectedRecipients)[0])?.label ?? "1 selected")
      : `${selectedRecipients.size} recipients selected`;

  const btnBase = "flex-1 py-1.5 rounded-lg text-sm font-semibold transition-all duration-150";
  const methodActive = (m: DeliveryMethod) => deliveryMethod === m;

  return (
    <div className="space-y-3">

      {/* ── Destination toggle ─────────────────────────────────────── */}
      <div>
        <label className="block text-sm font-semibold text-[oklch(0.08_0.01_240)] uppercase tracking-wider mb-1.5">
          Send To <span style={{ color: "oklch(0.08 0.01 240)" }}>*</span>
        </label>

        <div className="flex gap-2 mb-2">
          <button
            type="button"
            onClick={() => setSendToClient(false)}
            className={btnBase}
            style={{
              background: !sendToClient ? "oklch(0.72 0.14 75)" : "oklch(0.93 0.008 240)",
              border: `1px solid ${!sendToClient ? "oklch(0.65 0.14 75)" : "oklch(0.82 0.02 240)"}`,
              color: "oklch(0.08 0.01 240)",
            }}
          >
            Team Member
          </button>
          <button
            type="button"
            onClick={() => { setSendToClient(true); setSelectedRecipients(new Set()); setRecipientOpen(false); }}
            className={btnBase}
            style={{
              background: sendToClient ? "oklch(0.72 0.14 75)" : "oklch(0.93 0.008 240)",
              border: `1px solid ${sendToClient ? "oklch(0.65 0.14 75)" : "oklch(0.82 0.02 240)"}`,
              color: "oklch(0.08 0.01 240)",
            }}
          >
            Client / External
          </button>
        </div>

        {/* ── Team member multi-select ──────────────────────────────── */}
        {!sendToClient && (
          <div className="relative">
            <button
              type="button"
              onClick={() => setRecipientOpen((v) => !v)}
              className="w-full px-3 py-2 rounded-lg text-sm text-left flex items-center justify-between"
              style={{
                background: "oklch(0.97 0.01 220)",
                border: `1px solid ${selectedRecipients.size > 0 ? "oklch(0.92 0.005 240 / 50%)" : "oklch(1 0 0 / 10%)"}`,
                color: selectedRecipients.size > 0 ? "oklch(0.08 0.01 240)" : "oklch(0.35 0.02 240)",
              }}
            >
              <span className="truncate">{selectorLabel}</span>
              <svg
                className="w-4 h-4 shrink-0 ml-2 transition-transform"
                style={{ color: "oklch(0.35 0.02 240)", transform: recipientOpen ? "rotate(180deg)" : "rotate(0deg)" }}
                fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
              </svg>
            </button>

            {recipientOpen && (
              <div
                className="absolute z-20 w-full mt-1 rounded-xl shadow-xl overflow-hidden"
                style={{ background: "oklch(0.97 0.01 220)", border: "1px solid oklch(1 0 0 / 12%)" }}
              >
                <button
                  type="button"
                  onClick={toggleAll}
                  className="w-full flex items-center gap-3 px-3 py-2.5 text-sm font-semibold uppercase tracking-wider transition-colors hover:bg-[oklch(1_0_0/5%)]"
                  style={{ color: "oklch(0.08 0.01 240)", borderBottom: "1px solid oklch(1 0 0 / 8%)" }}
                >
                  <span
                    className="w-4 h-4 rounded flex items-center justify-center shrink-0"
                    style={{
                      background: selectedRecipients.size === TEAM_RECIPIENTS.length ? "oklch(0.72 0.14 75)" : "oklch(0.93 0.008 240)",
                      border: "1px solid oklch(0.82 0.02 240)",
                    }}
                  >
                    {selectedRecipients.size === TEAM_RECIPIENTS.length && (
                      <svg className="w-2.5 h-2.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                      </svg>
                    )}
                    {selectedRecipients.size > 0 && selectedRecipients.size < TEAM_RECIPIENTS.length && (
                      <span className="w-2 h-0.5 bg-[oklch(0.65_0.18_220)] rounded" />
                    )}
                  </span>
                  Select All
                </button>
                <div className="max-h-52 overflow-y-auto">
                  {TEAM_RECIPIENTS.map((r) => {
                    const checked = selectedRecipients.has(r.value);
                    return (
                      <button
                        key={r.value}
                        type="button"
                        onClick={() => toggleRecipient(r.value)}
                        className="w-full flex items-center gap-3 px-3 py-2 text-sm transition-colors hover:bg-[oklch(1_0_0/5%)]"
                        style={{ color: "oklch(0.08 0.01 240)" }}
                      >
                        <span
                          className="w-4 h-4 rounded flex items-center justify-center shrink-0"
                          style={{
                            background: checked ? "oklch(0.72 0.14 75)" : "oklch(0.93 0.008 240)",
                            border: `1px solid ${checked ? "oklch(0.65 0.14 75)" : "oklch(0.82 0.02 240)"}`,
                          }}
                        >
                          {checked && (
                            <svg className="w-2.5 h-2.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                            </svg>
                          )}
                        </span>
                        <span className="truncate">{r.label}</span>
                      </button>
                    );
                  })}
                </div>
                <div style={{ borderTop: "1px solid oklch(1 0 0 / 8%)" }}>
                  <button
                    type="button"
                    onClick={() => setRecipientOpen(false)}
                    className="w-full py-2 text-sm font-semibold transition-colors hover:bg-[oklch(1_0_0/5%)]"
                    style={{ color: "oklch(0.08 0.01 240)" }}
                  >
                    Done {selectedRecipients.size > 0 ? `(${selectedRecipients.size} selected)` : ""}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ── Client / External delivery fields ────────────────────── */}
        {sendToClient && (
          <div className="space-y-2">
            {/* Client name */}
            <input
              type="text"
              value={clientDisplayName}
              onChange={(e) => setClientDisplayName(e.target.value)}
              placeholder="Client name (optional)"
              autoComplete="off"
              className="w-full px-3 py-2 rounded-lg text-sm text-[oklch(0.18_0.04_240)] placeholder:text-[oklch(0.45_0.02_240)] outline-none"
              style={{ background: "oklch(0.97 0.01 220)", border: "1px solid oklch(1 0 0 / 10%)" }}
            />

            {/* Delivery method toggle */}
            <div>
              <p className="text-xs font-semibold text-[oklch(0.35_0.02_240)] uppercase tracking-wider mb-1.5">
                Delivery Method
              </p>
              <div className="flex gap-1.5">
                {(["email", "text", "both"] as DeliveryMethod[]).map((m) => (
                  <button
                    key={m}
                    type="button"
                    onClick={() => setDeliveryMethod(m)}
                    className="flex-1 py-1.5 rounded-lg text-xs font-semibold capitalize transition-all duration-150"
                    style={{
                      background: methodActive(m) ? "oklch(0.72 0.14 75)" : "oklch(0.93 0.008 240)",
                      border: `1px solid ${methodActive(m) ? "oklch(0.65 0.14 75)" : "oklch(0.82 0.02 240)"}`,
                      color: "oklch(0.08 0.01 240)",
                    }}
                  >
                    {m === "both" ? "Email + Text" : m === "text" ? "Text (MMS)" : "Email"}
                  </button>
                ))}
              </div>
              {/* Tax document warning banner */}
              {files.some((fi) => isTaxDocument(fi.file.name)) && deliveryMethod !== "text" && (
                <div
                  className="mt-2 px-3 py-2 rounded-lg text-xs font-semibold flex items-start gap-2"
                  style={{ background: "oklch(0.95 0.06 30)", border: "1px solid oklch(0.75 0.12 30)", color: "oklch(0.35 0.10 30)" }}
                >
                  <svg className="w-4 h-4 shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
                  </svg>
                  <span>APX Policy: Tax documents containing personal information (SSN, etc.) must be sent via <strong>Text (MMS) only</strong>. Email delivery is not permitted for these documents.</span>
                </div>
              )}
            </div>

            {/* Email field */}
            {(deliveryMethod === "email" || deliveryMethod === "both") && (
              <input
                type="email"
                value={clientEmail}
                onChange={(e) => setClientEmail(e.target.value)}
                placeholder="Client email address"
                autoComplete="off"
                className="w-full px-3 py-2 rounded-lg text-sm text-[oklch(0.18_0.04_240)] placeholder:text-[oklch(0.45_0.02_240)] outline-none"
                style={{ background: "oklch(0.97 0.01 220)", border: "1px solid oklch(1 0 0 / 10%)" }}
              />
            )}

            {/* Phone field */}
            {(deliveryMethod === "text" || deliveryMethod === "both") && (
              <input
                type="tel"
                value={clientPhone}
                onChange={(e) => setClientPhone(e.target.value)}
                placeholder="Client phone number (e.g. 3105551234)"
                autoComplete="off"
                className="w-full px-3 py-2 rounded-lg text-sm text-[oklch(0.18_0.04_240)] placeholder:text-[oklch(0.45_0.02_240)] outline-none"
                style={{ background: "oklch(0.97 0.01 220)", border: "1px solid oklch(1 0 0 / 10%)" }}
              />
            )}
          </div>
        )}
      </div>

      {/* ── Drop zone ─────────────────────────────────────────────── */}
      <div
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={handleDrop}
        onClick={() => inputRef.current?.click()}
        className="rounded-xl flex flex-col items-center justify-center cursor-pointer transition-all duration-200"
        style={{
          minHeight: compact ? "80px" : "120px",
          background: dragging ? "oklch(0.88 0.06 75 / 20%)" : "oklch(0.96 0.01 220)",
          border: `2px dashed ${dragging ? "oklch(0.72 0.14 75)" : "oklch(0.75 0.02 240 / 50%)"}`,
        }}
      >
        <input
          ref={inputRef}
          type="file"
          multiple
          className="hidden"
          autoComplete="off"
          onChange={(e) => addFiles(Array.from(e.target.files ?? []))}
        />
        <svg
          className="w-6 h-6 mb-2"
          style={{ color: pasting ? "oklch(0.45 0.18 140)" : dragging ? "oklch(0.45 0.14 75)" : "oklch(0.45 0.04 240)" }}
          fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}
        >
          {pasting
            ? <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            : <path strokeLinecap="round" strokeLinejoin="round" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />}
        </svg>
        <p className="text-sm font-medium text-[oklch(0.25_0.04_240)]">
          {pasting ? "✓ File pasted!" : dragging ? "Drop to attach" : "Drag & drop, click to browse, or paste (Ctrl+V / Cmd+V)"}
        </p>
        <p className="text-sm text-[oklch(0.45_0.03_240)] mt-0.5">{pasting ? "" : "Max 16MB per file"}</p>
      </div>

      {/* ── File list ─────────────────────────────────────────────── */}
      {files.length > 0 && (
        <div className="space-y-1.5">
          {files.map(({ file, id }) => (
            <div
              key={id}
              className="flex items-center gap-2 px-3 py-2 rounded-lg"
              style={{ background: "oklch(0.97 0.01 220)" }}
            >
              <svg className="w-4 h-4 shrink-0 text-[oklch(0.08_0.01_240)]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <span className="text-sm text-[oklch(0.08_0.01_240)] flex-1 truncate">{file.name}</span>
              <span className="text-sm text-[oklch(0.35_0.02_240)] shrink-0">{formatBytes(file.size)}</span>
              <button
                type="button"
                onClick={() => setFiles((prev) => prev.filter((f) => f.id !== id))}
                className="shrink-0 text-[oklch(0.35_0.02_240)] hover:text-[oklch(0.08_0.01_240)] transition-colors"
              >
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          ))}
        </div>
      )}

      {/* ── Note ──────────────────────────────────────────────────── */}
      {files.length > 0 && (
        <input
          type="text"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder={sendToClient ? "Add a message / caption (optional)…" : "Add a note (optional)…"}
          autoComplete="off"
          className="w-full px-3 py-2 rounded-lg text-sm text-[oklch(0.18_0.04_240)] placeholder:text-[oklch(0.35_0.02_240)] outline-none"
          style={{ background: "oklch(0.97 0.01 220)", border: "1px solid oklch(1 0 0 / 10%)" }}
        />
      )}

      {/* ── Send button ───────────────────────────────────────────── */}
      <button
        type="button"
        onClick={handleSend}
        disabled={!canSend}
        className="w-full py-2.5 rounded-lg text-sm font-bold transition-all duration-150"
        style={{
          background: canSend ? "oklch(0.72 0.14 75)" : "oklch(0.92 0.01 240)",
          color: canSend ? "oklch(0.12 0.02 240)" : "oklch(0.50 0.03 240)",
          cursor: canSend ? "pointer" : "not-allowed",
          border: canSend ? "1px solid oklch(0.65 0.14 75)" : "1px solid oklch(0.80 0.02 240)",
        }}
      >
        {uploading
          ? "Sending…"
          : files.length === 0
          ? "Select files to send"
          : !canSend
          ? sendToClient
            ? deliveryMethod === "both"
              ? "Enter email and phone to send"
              : deliveryMethod === "email"
              ? "Enter client email to send"
              : "Enter client phone to send"
            : "Choose a recipient first"
          : sendToClient
          ? `Send ${files.length} file${files.length > 1 ? "s" : ""} via ${deliveryMethod === "both" ? "Email + Text" : deliveryMethod === "text" ? "Text (MMS)" : "Email"}`
          : `Send ${files.length} file${files.length > 1 ? "s" : ""} to ${effectiveRecipients.length} recipient${effectiveRecipients.length > 1 ? "s" : ""}`}
      </button>
    </div>
  );
}
